import { useEffect, useRef, useState } from "react";

const MIN_MS = 600;   // hold the loader at least this long so it's seen
const MAX_MS = 9000;  // safety: never hang, even if media stalls/404s

/**
 * Preloads a set of images (+ optional video) and reports progress. Errors/404s
 * count as "loaded" so the gate can never hang; a min display time avoids a
 * flash, a max time forces release. Shared by every premium join loader via
 * `PremiumLayoutHost`.
 *
 * Mirrors the new Image() + load/error pattern in useScrollFrameSequence.ts.
 */
export function useMediaPreload(
  urls: string[],
  videoUrl?: string,
): { progress: number; ready: boolean } {
  const [loaded, setLoaded] = useState(0);
  const [ready, setReady] = useState(false);
  const startRef = useRef(Date.now());
  const total = urls.length + (videoUrl ? 1 : 0);

  useEffect(() => {
    let cancelled = false;
    let count = 0;
    const bump = () => {
      if (cancelled) return;
      count += 1;
      setLoaded(count);
    };

    if (total === 0) {
      const t = setTimeout(() => !cancelled && setReady(true), MIN_MS);
      return () => { cancelled = true; clearTimeout(t); };
    }

    urls.forEach((src) => {
      let settled = false;
      const done = () => { if (!settled) { settled = true; bump(); } };
      const img = new Image();
      img.decoding = "async";
      img.onload = done;
      img.onerror = done;
      img.src = src;
      if (img.complete) done(); // already cached
    });

    let video: HTMLVideoElement | undefined;
    if (videoUrl) {
      let settled = false;
      const done = () => { if (!settled) { settled = true; bump(); } };
      video = document.createElement("video");
      video.preload = "auto";
      video.muted = true;
      video.onloadeddata = done;
      video.onerror = done;
      video.src = videoUrl;
    }

    const maxTimer = setTimeout(() => !cancelled && setReady(true), MAX_MS);

    return () => {
      cancelled = true;
      clearTimeout(maxTimer);
      if (video) {
        video.onloadeddata = null;
        video.onerror = null;
        video.src = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urls, videoUrl, total]);

  // Release once everything is in AND the min beat has passed.
  useEffect(() => {
    if (ready || total === 0) return;
    if (loaded >= total) {
      const remain = Math.max(0, MIN_MS - (Date.now() - startRef.current));
      const t = setTimeout(() => setReady(true), remain);
      return () => clearTimeout(t);
    }
  }, [loaded, total, ready]);

  return { progress: total === 0 ? 1 : Math.min(1, loaded / total), ready };
}
